import _, {isArray, isEqual, isNumber, isUndefined, last, min, omit} from 'lodash';

import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {ScrollDirectionsEnum} from '../../../../srcCommon/scroll/scrollerTypes';
import {
  CellsBoundary,
  CellsConversationsBoundary,
  CellsMessagesBoundary,
  compareBoundaries,
  isWithinBounds,
  makeConversationBoundaryBuilder,
  makeMessageBoundaryBuilder,
} from '../../../helpers/cellsHelpers';
import {
  findDraftCommentForLastMessage,
  isDirtyDraftMessage,
  isUntouchedDraftMessage,
} from '../../../helpers/models/messageHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {DataGroupModel} from '../../../models/api/dataGroupModel';
import {MessageModel} from '../../../models/api/messageModel';
import {CellsSortDirectionsEnum} from '../../../models/api/sortOptionModel';
import {TeammatesFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {AppState} from '../../appState';
import {getComments, getConversations, getMessages} from '../../data/dataSelectors';
import {CellsContext, CellsContextTypesEnum} from '../../panels/cells/context/cellsContext';
import {isConversationInPanel, isMessageInPanel} from '../../panels/cells/helpers/cellsPanelHelpers';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesTeammateMessagesParameters,
} from '../../panels/inboxes/parameters/inboxesParameters';
import {
  areParametersCellsEqual,
  findFirstSelectedId,
  pickCellsParameters,
} from '../../panels/inboxes/parameters/inboxesParametersHelpers';
import {
  CellsConversationsListRange,
  CellsListRange,
  CellsListState,
  CellsMessagesListRange,
  CellsSyncContext,
} from './cellsList/cellsListState';
import {getCellsLists} from './cellsListsSelectors';
import {CellsListsState, filterCellsListTypeForParameters} from './cellsListsState';

/*
 * Constants.
 */

/**
 * Worst-case estimation of how far behind Elasticsearch is.
 *
 * This causes us to sync with overlap, and it effectively increases our trust in data coming from pusher
 * events, non-ES-backed API calls, etc.
 */
const apiCellsFetchedAtOffset = -30 * 1000; // ms

/*
 * Types.
 */

export type HasFetchedAt<T> = T & {
  fetchedAt: number;
};

export type MaybeFetchedAt<T extends HasFetchedAt<any>> = Omit<T, 'fetchedAt'> & {
  fetchedAt: number | undefined;
};

/*
 * Helpers.
 */

export function hasFetchedAt<T>(obj: T): obj is HasFetchedAt<T> {
  return Boolean(obj && hasProperty(obj, 'fetchedAt') && isNumber(obj.fetchedAt));
}

export function postProcessApiCellsDataGroupFetchedAt<T extends DataGroupModel>(dataGroup: T): T {
  return postProcessApiDataGroupFetchedAt(dataGroup, apiCellsFetchedAtOffset);
}

export function postProcessApiDataGroupFetchedAt<T extends DataGroupModel>(
  dataGroup: T,
  fetchedAtOffset: number,
): T {
  if (!hasFetchedAt(dataGroup)) {
    return dataGroup;
  }

  const fetchedAt = dataGroup.fetchedAt + fetchedAtOffset;
  return {
    ...dataGroup,
    fetchedAt,
  };
}

export function canSyncCellsContext(cellsContext: CellsContext): boolean {
  return Boolean(makeCellsSyncContext(cellsContext));
}

export function makeCellsSyncContext(cellsContext: CellsContext): CellsSyncContext | undefined {
  // Sync only personal workspace.
  if (
    cellsContext.type === CellsContextTypesEnum.TEAMMATE &&
    !cellsContext.searchQuery &&
    !cellsContext.isDelegation
  ) {
    return {teammateId: cellsContext.teammateId};
  }

  return undefined;
}

export function isEqualCellsSyncContext(a: CellsSyncContext, b: CellsSyncContext): boolean {
  return isEqual(a, b);
}

/** Sorts ranges by their top boundary and sync date. */
export function compareCellsListRanges<TId>(
  sortDirection: CellsSortDirectionsEnum,
  a: CellsListRange<TId>,
  b: CellsListRange<TId>,
): number {
  // Sort by the top boundary.
  const topBoundaryDiff = compareCellsListTopBoundaries(sortDirection, a.topBoundary, b.topBoundary);
  if (topBoundaryDiff !== 0) {
    return topBoundaryDiff;
  }

  // Sort by most recently synced or fetched.
  return compareCellsListRangesMostRecent(a, b);
}

export function compareCellsListTopBoundaries<TId>(
  sortDirection: CellsSortDirectionsEnum,
  a: CellsBoundary<TId> | undefined,
  b: CellsBoundary<TId> | undefined,
) {
  // Sort by the presence of a top boundary.
  if (!a && b) {
    return -1;
  }

  if (a && !b) {
    return 1;
  }

  // Sort by the top boundary.
  return compareBoundaries(sortDirection, a, b);
}

export function compareCellsListRangesMostRecent<TId>(
  a: CellsListRange<TId>,
  b: CellsListRange<TId>,
): number {
  // Sort by most recently synced.
  const syncedAtDiff = (b.syncedAt || b.fetchedAt) - (a.syncedAt || a.fetchedAt);
  if (syncedAtDiff !== 0) {
    return syncedAtDiff;
  }

  // Sort by most recently fetched.
  return b.fetchedAt - a.fetchedAt;
}

export function isStrictlyIntersectingRangeBottom<TId>(
  sortDirection: CellsSortDirectionsEnum,
  prevRange: CellsListRange<TId>,
  nextRange: CellsListRange<TId>,
): boolean {
  return Boolean(
    // We must have a previous range with a finite bottom.
    prevRange.bottomBoundary &&
      // The next top boundary must be equal to or less than the previous bottom boundary.
      compareBoundaries(sortDirection, nextRange.topBoundary, prevRange.bottomBoundary) <= 0 &&
      // The next bottom boundary must be greater than the previous bottom boundary.
      (!nextRange.bottomBoundary ||
        compareBoundaries(sortDirection, prevRange.bottomBoundary, nextRange.bottomBoundary) < 0),
  );
}

export function isCellsListWritableForParameters(
  list: CellsListState | undefined,
  parameters: InboxesParameters,
): boolean {
  if (!list) {
    return false;
  }

  return areParametersCellsEqual(list.parameters, parameters);
}

export enum CellsListMatchResultsEnum {
  /** Disjoint set: this list does not contain any items matching the parameters. */
  DISJOINT = 'DISJOINT',
  /** Equal set: this list contains exactly all items matching the parameters. */
  EQUAL = 'EQUAL',
  /** Superset: this list contains at least all items matching the parameters. */
  SUPERSET = 'SUPERSET',
}

const teammatesFoldersReadableFromInbox = new Set([
  TeammatesFoldersEnum.ASSIGNED,
  TeammatesFoldersEnum.SHARED_WITH_ME,
  TeammatesFoldersEnum.DISCUSSIONS,
]);

export function matchCellsListToParameters(
  list: CellsListState | undefined,
  cellsContext: CellsContext,
  parameters: InboxesParameters,
): CellsListMatchResultsEnum {
  if (!list) {
    return CellsListMatchResultsEnum.DISJOINT;
  }

  const listParameters = list.parameters;
  const normalizedListParameters = pickCellsParameters(listParameters);
  const normalizedParameters = pickCellsParameters(parameters);

  // Are we reading from the user inbox?
  if (
    listParameters.type === InboxesParametersTypesEnum.USER &&
    listParameters.folder === TeammatesFoldersEnum.INBOX
  ) {
    // Are we fetching user inbox subfolders?
    if (
      parameters.type === InboxesParametersTypesEnum.USER &&
      teammatesFoldersReadableFromInbox.has(parameters.folder) &&
      isEqual(omit(normalizedListParameters, 'folder'), omit(normalizedParameters, 'folder'))
    ) {
      return CellsListMatchResultsEnum.SUPERSET;
    }

    // Are we fetching a private inbox belonging to the user?
    if (
      parameters.type === InboxesParametersTypesEnum.TEAMMATE_INBOX &&
      isInboxOwnedByTeammate(cellsContext, parameters.inboxId, listParameters.teammateId) &&
      isEqual(
        omit(normalizedListParameters, ['type', 'teammateId', 'folder']),
        omit(normalizedParameters, ['type', 'inboxId']),
      )
    ) {
      return CellsListMatchResultsEnum.SUPERSET;
    }
  }

  // Are the parameters equal?
  if (isEqual(normalizedListParameters, normalizedParameters)) {
    return CellsListMatchResultsEnum.EQUAL;
  }

  return CellsListMatchResultsEnum.DISJOINT;
}

export function isCellsListReadableForParameters(
  list: CellsListState | undefined,
  cellsContext: CellsContext,
  parameters: InboxesParameters,
): boolean {
  const matchResult = matchCellsListToParameters(list, cellsContext, parameters);
  return (
    matchResult === CellsListMatchResultsEnum.EQUAL || matchResult === CellsListMatchResultsEnum.SUPERSET
  );
}

export function findCellsListReadableForParameters<T extends InboxesParameters>(
  state: AppState,
  cellsContext: CellsContext,
  parameters: T,
) {
  const allLists = getCellsLists(state);
  const listsOfType = filterCellsListTypeForParameters(allLists, parameters);
  let winningList: CellsListState<T> | undefined;

  // Prefer equal matches over superset matches.
  for (let i = 0; i < listsOfType.length; i++) {
    const list = listsOfType[i];
    const matchResult = matchCellsListToParameters(list, cellsContext, parameters);

    // If we found an equal match, we're done.
    if (matchResult === CellsListMatchResultsEnum.EQUAL) {
      winningList = list;
      break;
    }

    // If we found the first superset match, keep it as a candidate.
    if (matchResult === CellsListMatchResultsEnum.SUPERSET && !winningList) {
      winningList = list;
    }
  }

  return winningList;
}

function isInboxOwnedByTeammate(cellsContext: CellsContext, inboxId: number, teammateId: number) {
  return cellsContext.inboxId === inboxId && cellsContext.teammateId === teammateId;
}

export function sliceCellsConversationsInitial(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
) {
  const firstSelectedId = findFirstSelectedId(parameters.selection);
  if (isUndefined(firstSelectedId)) {
    return sliceCellsConversationsTop(state, cellsContext, parameters);
  }

  return sliceCellsConversationsAround(state, cellsContext, parameters, firstSelectedId);
}

export function sliceCellsMessagesInitial(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
) {
  const firstSelectedId = findFirstSelectedId(parameters.selection);
  if (isUndefined(firstSelectedId)) {
    return sliceCellsMessagesTop(state, cellsContext, parameters);
  }

  return sliceCellsMessagesAround(state, cellsContext, parameters, firstSelectedId);
}

/** Fetches the top of a P2 list from the local store. */
export function sliceCellsConversationsTop(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
): CellsConversationsListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  // Find the top of the list.
  const fetchRanges = selectCellsListRangesTop(list.parameters.sortDirection, list.ranges);

  // Fetch the conversations.
  return sliceCellsConversations(state, cellsContext, parameters, fetchRanges);
}

export function sliceCellsMessagesTop(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
): CellsMessagesListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  // Find the top of the list.
  const fetchRanges = selectCellsListRangesTop(list.parameters.sortDirection, list.ranges);

  // Fetch the messages.
  return sliceCellsMessages(state, parameters, fetchRanges);
}

export function selectCellsListRangesTop<TId>(
  sortDirection: CellsSortDirectionsEnum,
  sortedRanges: ReadonlyArray<CellsListRange<TId>>,
): ReadonlyArray<CellsListRange<TId>> {
  return sortedRanges.reduce((selectedRanges: Array<CellsListRange<TId>>, range) => {
    const prevRange = last(selectedRanges);

    // Start with a range anchored at the top.
    if (!prevRange && !range.topBoundary) {
      selectedRanges.push(range);
      return selectedRanges;
    }

    // Append any partially overlapping ranges that are at least as recent.
    if (
      prevRange &&
      isStrictlyIntersectingRangeBottom(sortDirection, prevRange, range) &&
      range.fetchedAt >= prevRange.fetchedAt
    ) {
      selectedRanges.push(range);
      return selectedRanges;
    }

    return selectedRanges;
  }, []);
}

/** Fetches the P2 list around a conversation from the local store. */
export function sliceCellsConversationsAround(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  aroundId: number,
): CellsConversationsListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  // Find the conversation in the store.
  const allConversations = getConversations(state);
  const aroundConversation = allConversations[aroundId];
  if (!aroundConversation) {
    return undefined;
  }

  // Find the most recent cached range that includes this conversation.
  const boundaryBuilder = makeConversationBoundaryBuilder(list.cellsContext, list.parameters);
  const aroundBoundary = boundaryBuilder(aroundConversation);
  const fetchRanges = selectCellsListRangesAround(list.parameters.sortDirection, list.ranges, aroundBoundary);

  // Fetch the conversations.
  return sliceCellsConversations(state, cellsContext, parameters, fetchRanges);
}

export function sliceCellsMessagesAround(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
  aroundUid: string,
): CellsMessagesListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  // Find the message in the store.
  const allMessages = getMessages(state);
  const aroundMessage = allMessages[aroundUid];
  if (!aroundMessage) {
    return undefined;
  }

  // Find the most recent cached range that includes this message.
  const boundaryBuilder = makeMessageBoundaryBuilder(list.parameters);

  const aroundBoundary = boundaryBuilder(aroundMessage);
  const fetchRanges = selectCellsListRangesAround(list.parameters.sortDirection, list.ranges, aroundBoundary);

  // Fetch the messages.
  return sliceCellsMessages(state, parameters, fetchRanges);
}

export function selectCellsListRangesAround<TId>(
  sortDirection: CellsSortDirectionsEnum,
  ranges: ReadonlyArray<CellsListRange<TId>>,
  aroundBoundary: CellsBoundary<TId>,
): ReadonlyArray<CellsListRange<TId>> {
  // TODO: Expand to overlapping ranges?
  const mostRecentlyFetchedRange = _(ranges)
    .filter(
      ({topBoundary, bottomBoundary}) =>
        compareBoundaries(sortDirection, topBoundary, aroundBoundary) <= 0 &&
        compareBoundaries(sortDirection, aroundBoundary, bottomBoundary) <= 0,
    )
    .maxBy((range) => range.fetchedAt);

  // Default to a list containing only this item.
  const range = mostRecentlyFetchedRange || {
    topBoundary: aroundBoundary,
    bottomBoundary: aroundBoundary,
    ids: [aroundBoundary.id],
    fetchedAt: 0,
  };

  return [range];
}

/** Fetches the P2 list before/after a conversation. */
export function sliceCellsConversationsMore(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsConversationsBoundary,
): CellsConversationsListRange | undefined {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return sliceCellsConversationsBefore(state, cellsContext, parameters, referenceBoundary);

    case ScrollDirectionsEnum.BOTTOM:
      return sliceCellsConversationsAfter(state, cellsContext, parameters, referenceBoundary);

    default:
      ensureNeverDirection(direction);
      throw new Error(`Invalid scroll direction ${direction}`);
  }
}

/** Fetches the P2 list before/after a conversation. */
export function sliceCellsMessagesMore(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsMessagesBoundary,
): CellsMessagesListRange | undefined {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return sliceCellsMessagesBefore(state, cellsContext, parameters, referenceBoundary);

    case ScrollDirectionsEnum.BOTTOM:
      return sliceCellsMessagesAfter(state, cellsContext, parameters, referenceBoundary);

    default:
      ensureNeverDirection(direction);
      throw new Error(`Invalid scroll direction ${direction}`);
  }
}

function ensureNeverDirection(_direction: never) {}

/** Fetches the P2 list before a conversation. */
export function sliceCellsConversationsBefore(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  referenceBoundary: CellsConversationsBoundary,
): CellsConversationsListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  const fetchRanges = selectCellsListRangesBefore(
    list.parameters.sortDirection,
    list.ranges,
    referenceBoundary,
  );

  return sliceCellsConversations(state, cellsContext, parameters, fetchRanges);
}

export function sliceCellsMessagesBefore(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
  referenceBoundary: CellsMessagesBoundary,
): CellsMessagesListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  const fetchRanges = selectCellsListRangesBefore(
    list.parameters.sortDirection,
    list.ranges,
    referenceBoundary,
  );

  return sliceCellsMessages(state, parameters, fetchRanges);
}

export function selectCellsListRangesBefore<TId>(
  sortDirection: CellsSortDirectionsEnum,
  sortedRanges: ReadonlyArray<CellsListRange<TId>>,
  referenceBoundary: CellsBoundary<TId>,
): ReadonlyArray<CellsListRange<TId>> {
  return _(sortedRanges).reduceRight((selectedRanges: Array<CellsListRange<TId>>, range) => {
    const prevRange = last(selectedRanges);

    // Start with a range that contains, but does not start at, this conversation.
    if (
      !prevRange &&
      (!range.topBoundary || compareBoundaries(sortDirection, range.topBoundary, referenceBoundary) < 0) &&
      compareBoundaries(sortDirection, referenceBoundary, range.bottomBoundary) <= 0
    ) {
      selectedRanges.unshift({
        ...range,
        bottomBoundary: referenceBoundary,
      });
      return selectedRanges;
    }

    // Prepend any preceding overlapping ranges that are at least as recent.
    if (
      prevRange &&
      prevRange.topBoundary &&
      compareBoundaries(sortDirection, prevRange.topBoundary, range.bottomBoundary) <= 0 &&
      (!range.topBoundary ||
        compareBoundaries(sortDirection, range.topBoundary, prevRange.bottomBoundary) < 0) &&
      range.fetchedAt >= prevRange.fetchedAt
    ) {
      selectedRanges.unshift(range);
      return selectedRanges;
    }

    return selectedRanges;
  }, []);
}

export function sliceCellsConversationsAfter(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  referenceBoundary: CellsConversationsBoundary,
): CellsConversationsListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  const fetchRanges = selectCellsListRangesAfter(
    list.parameters.sortDirection,
    list.ranges,
    referenceBoundary,
  );

  return sliceCellsConversations(state, cellsContext, parameters, fetchRanges);
}

export function sliceCellsMessagesAfter(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesTeammateMessagesParameters,
  referenceBoundary: CellsMessagesBoundary,
): CellsMessagesListRange | undefined {
  // Find a cached list of cells.
  const list = findCellsListReadableForParameters(state, cellsContext, parameters);
  if (!list) {
    return undefined;
  }

  const fetchRanges = selectCellsListRangesAfter(
    list.parameters.sortDirection,
    list.ranges,
    referenceBoundary,
  );

  return sliceCellsMessages(state, parameters, fetchRanges);
}

export function selectCellsListRangesAfter<TId>(
  sortDirection: CellsSortDirectionsEnum,
  sortedRanges: ReadonlyArray<CellsListRange<TId>>,
  referenceBoundary: CellsBoundary<TId>,
): ReadonlyArray<CellsListRange<TId>> {
  return sortedRanges.reduce((selectedRanges: Array<CellsListRange<TId>>, range) => {
    const prevRange = last(selectedRanges);

    // Start with a range that contains, but does not end at, this conversation.
    if (
      !prevRange &&
      compareBoundaries(sortDirection, range.topBoundary, referenceBoundary) <= 0 &&
      (!range.bottomBoundary || compareBoundaries(sortDirection, referenceBoundary, range.bottomBoundary) < 0)
    ) {
      selectedRanges.push({
        ...range,
        topBoundary: referenceBoundary,
      });
      return selectedRanges;
    }

    // Append any partially overlapping ranges that are at least as recent.
    if (
      prevRange &&
      isStrictlyIntersectingRangeBottom(sortDirection, prevRange, range) &&
      range.fetchedAt >= prevRange.fetchedAt
    ) {
      selectedRanges.push(range);
      return selectedRanges;
    }

    return selectedRanges;
  }, []);
}

export function sliceCellsMessagesOffline(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  topBoundary?: CellsMessagesBoundary,
  bottomBoundary?: CellsMessagesBoundary,
): CellsMessagesListRange {
  const fetchRange = {
    topBoundary,
    bottomBoundary,
    ids: [],
    fetchedAt: 0,
  };

  return sliceCellsMessages(state, parameters, fetchRange);
}

export function sliceCellsMessagesOfflineMore(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  direction: ScrollDirectionsEnum,
  referenceBoundary: CellsMessagesBoundary,
): CellsMessagesListRange {
  switch (direction) {
    case ScrollDirectionsEnum.TOP:
      return {
        ...sliceCellsMessagesOffline(state, parameters, undefined, referenceBoundary),
        // Keep the boundary the same since this is not a truly correct P2 fetch.
        topBoundary: referenceBoundary,
      };

    case ScrollDirectionsEnum.BOTTOM:
      return {
        ...sliceCellsMessagesOffline(state, parameters, referenceBoundary, undefined),
        // Keep the boundary the same since this is not a truly correct P2 fetch.
        bottomBoundary: referenceBoundary,
      };

    default:
      ensureNeverDirection(direction);
      throw new Error(`Invalid scroll direction ${direction}`);
  }
}

export function sliceCellsConversations(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  rangeOrRanges: CellsConversationsListRange,
): CellsConversationsListRange;
export function sliceCellsConversations(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  rangeOrRanges: ReadonlyArray<CellsConversationsListRange>,
): CellsConversationsListRange | undefined;
export function sliceCellsConversations(
  state: AppState,
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
  rangeOrRanges: CellsConversationsListRange | ReadonlyArray<CellsConversationsListRange>,
): CellsConversationsListRange | undefined {
  const ranges = isArray(rangeOrRanges) ? rangeOrRanges : [rangeOrRanges];
  if (ranges.length === 0) {
    return undefined;
  }

  const allConversations = getConversations(state);
  const boundaryBuilder = makeConversationBoundaryBuilder(cellsContext, parameters);
  const {sortDirection} = parameters;

  const ids = _(allConversations)
    .map((conversation) => {
      if (!conversation) {
        return undefined;
      }

      // Does the conversation match the parameters?
      if (!isConversationInPanel(state.data, cellsContext, parameters, conversation)) {
        return undefined;
      }

      // This requires the conversation to match the filter, because we may need a tracker for the sort date.
      const boundary = boundaryBuilder(conversation);

      // Is the conversation in one of the ranges?
      const isInRange = ranges.some(
        (range) =>
          isWithinBounds(range, sortDirection, boundary) &&
          (isConversationSyncedInRange(range, conversation) ||
            isConversationFetchedInRange(range, conversation)),
      );

      return isInRange ? boundary : undefined;
    })
    .compact()
    .sort((a, b) => compareBoundaries(sortDirection, a, b))
    .map((boundary) => boundary.id)
    .value();

  return {
    topBoundary: ranges[0].topBoundary,
    bottomBoundary: ranges[ranges.length - 1].bottomBoundary,
    ids,
    fetchedAt: min(ranges.map((range) => range.fetchedAt)) as number,
  };
}

function isConversationSyncedInRange(range: CellsConversationsListRange, conversation: ConversationModel) {
  // If the conversation was updated from an API response, push event, or P2 sync, then it is eligible to be included.
  return conversation.updatedAt >= range.fetchedAt;
}

function isConversationFetchedInRange(range: CellsConversationsListRange, conversation: ConversationModel) {
  // If the conversation was returned by the server in a P2 fetch, then it is elgible to be included.
  return range.ids.includes(conversation.id);
}

export function sliceCellsMessages(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  range: CellsMessagesListRange,
): CellsMessagesListRange;
export function sliceCellsMessages(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  ranges: ReadonlyArray<CellsMessagesListRange>,
): CellsMessagesListRange | undefined;
export function sliceCellsMessages(
  state: AppState,
  parameters: InboxesTeammateMessagesParameters,
  rangeOrRanges: CellsMessagesListRange | ReadonlyArray<CellsMessagesListRange>,
): CellsMessagesListRange | undefined {
  const ranges = isArray(rangeOrRanges) ? rangeOrRanges : [rangeOrRanges];
  if (ranges.length === 0) {
    return undefined;
  }

  const allMessages = getMessages(state);
  const allConversations = getConversations(state);
  const allComments = getComments(state);

  const boundaryBuilder = makeMessageBoundaryBuilder(parameters);
  const {sortDirection} = parameters;

  const ids = _(allMessages)
    .map((message) => {
      if (!message) {
        return undefined;
      }

      const draftComment = findDraftCommentForLastMessage(
        parameters,
        state.data.userId,
        message,
        allConversations,
        allComments,
      );

      // Does the message match the parameters?
      if (!isMessageInPanel(state.data, parameters, message, draftComment)) {
        return undefined;
      }

      const boundary = boundaryBuilder(message, draftComment);

      // Is the message in one of the ranges?
      const isInRange = ranges.some(
        (range) =>
          isWithinBounds(range, sortDirection, boundary) &&
          (isUntouchedDraftMessage(message) ||
            isDirtyDraftMessage(message) ||
            isMessageSyncedInRange(range, message) ||
            isMessageFetchedInRange(range, message)),
      );

      return isInRange ? boundary : undefined;
    })
    .compact()
    .sort((a, b) => compareBoundaries(sortDirection, a, b))
    .map((boundary) => boundary.id)
    .value();

  return {
    topBoundary: ranges[0].topBoundary,
    bottomBoundary: ranges[ranges.length - 1].bottomBoundary,
    ids,
    fetchedAt: min(ranges.map((range) => range.fetchedAt)) as number,
  };
}

function isMessageSyncedInRange(range: CellsMessagesListRange, message: MessageModel) {
  // If the message was updated from an API response, push event, or P2 sync, then it is eligible to be included.
  return !isUndefined(message.updatedAt) && message.updatedAt >= range.fetchedAt;
}

function isMessageFetchedInRange(range: CellsMessagesListRange, message: MessageModel) {
  // If the message was returned by the server in a P2 fetch, then it is elgible to be included.
  return range.ids.includes(message.uid);
}

export function computeCellsListsMinSyncedAt(lists: CellsListsState, context: CellsSyncContext) {
  return (
    _(lists)
      .filter((list) => isEqualCellsSyncContext(context, list.cellsSyncContext))
      // TODO: Fix lodash typings.
      .flatMap<CellsListRange<unknown>>((list) => list.ranges)
      .map((range) => range.syncedAt || range.fetchedAt)
      .min()
  );
}
